<template>
  <div class="custom-select-box relative mt-2 pl-2">
    <button
      :id="'js-button-box-toggle-' + listName"
      type="button"
      class="button-box mt-1 pl-2"
      :aria-label="
        selectionEnabled
          ? `${t('reader.close')}  ${t('reader.selector')} ${listLabel}`
          : `${t('reader.open')}  ${t('reader.selector')} ${listLabel}`
      "
      :title="
        selectionEnabled
          ? `${t('reader.close')}  ${t('reader.selector')} ${listLabel}`
          : `${t('reader.open')}  ${t('reader.selector')} ${listLabel}`
      "
      @click="toggleItemsBox"
    >
      {{
        labelTranslation
          ? translateLabel()
          : currentSelected === "Default"
            ? t("reader.defaultFontName")
            : currentSelected
      }}
    </button>

    <div
      :id="'js-custom-items-box-' + listName"
      class="items-list flex-wrap"
      role="radiogroup"
      :aria-label="t('settings.radioGroupSelect') + listLabel"
    >
      <template v-for="item in itemsList" :key="item.value">
        <input
          :id="'custom-list-' + listName + '-' + item.value"
          v-model="currentSelected"
          type="radio"
          class="hide-input"
          :name="listName"
          :value="item.value"
          :tabindex="selectionEnabled ? '0' : '-1'"
          @keypress.enter="toggleItemsBox"
          @keydown.esc="toggleItemsBox"
        />
        <label
          :for="'custom-list-' + listName + '-' + item.value"
          class="item-square-selector relative p-2 flex items-center block px-6 py-2 border-b w-full focus:outline-none focus:ring-0 transition duration-200 cursor-pointer"
          :title="
            labelTranslation
              ? t(`tts.countryLabel.${item.value}`)
              : item.label === 'Default'
                ? t('reader.defaultFontName')
                : item.label
          "
          @mouseup="toggleItemsBox"
        >
          <span class="font" :class="'font--' + item.value">{{
            labelTranslation
              ? t(`tts.countryLabel.${item.value}`)
              : item.label === "Default"
                ? t("reader.defaultFontName")
                : item.label
          }}</span>
        </label>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { settingValue } from "@/helpers/types";
import { ttsLanguageOptions } from "@/helpers/constants";

const { t } = useI18n();
const selectionEnabled = ref<boolean>(false);

const currentSelected = defineModel<string>({
  default: "Default",
});

const props = defineProps({
  itemsList: {
    type: Array as PropType<Array<settingValue>>,
    default: null,
  },
  listName: {
    type: String,
    default: "",
  },
  listLabel: {
    type: String,
    default: "",
  },
  labelTranslation: {
    type: Boolean,
    default: false,
  },
});

function toggleItemsBox(e: Event) {
  e.stopImmediatePropagation();
  const toggleButton = document.getElementById(
    "js-button-box-toggle-" + props.listName
  );
  const itemsBox = document.getElementById(
    "js-custom-items-box-" + props.listName
  );
  const items = document.getElementsByName(props.listName);
  const getItemActive = Array.from(items).find((item: HTMLElement) => {
    return item.checked === true;
  });

  toggleButton.classList.toggle("button-box--open");
  itemsBox.classList.toggle("items-list--show");

  if (itemsBox.classList.contains("items-list--show")) {
    selectionEnabled.value = true;
    if (getItemActive) {
      getItemActive.focus();
    } else {
      items.item(0).focus();
    }
  } else {
    toggleButton.focus();
    selectionEnabled.value = false;
  }
}

function translateLabel() {
  const labelKey = ttsLanguageOptions.find(
    (lang) => lang.value === currentSelected.value
  )?.value;

  if (labelKey) return t("tts.countryLabel." + labelKey);
  return undefined;
}
</script>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;

.custom-select-box {
  max-width: 18rem;
  font-size: 0.85rem;

  .button-box {
    position: relative;
    width: 100%;
    max-width: 15rem;
    height: 3rem;
    text-align: left;
    padding: 0 2rem 0 1rem;
    border: $spacingXS solid var(--DM__theme-hover-color);
    &::after {
      content: "expand_more";
      position: absolute;
      width: $spacingL;
      right: $spacingM;
      top: 50%;
      transform: translateY(-50%);
      z-index: -1;
      font-size: 1.2rem;
      font-family: "Material Icons";
      font-feature-settings: "liga";
      -webkit-font-feature-settings: "liga";
      transition: all 0.2s;
    }
    @media (any-hover: hover) {
      &:hover {
        border-color: var(--DM__theme-slider-color);
      }
    }
    &:focus {
      border-color: var(--DM__theme-slider-color);
      outline: 0;
      &:not(:focus-visible) {
        border-color: var(--DM__theme-slider-color);
      }
    }
    &--open {
      &::after {
        transform: translateY(-52%) rotate(180deg);
      }
    }
  }

  .items-list {
    display: flex;
    flex-wrap: wrap;
    width: 15rem;
    top: 5%;
    left: 35px;
    max-height: 0;
    border: $spacingXS solid transparent;
    border-top: none;
    overflow: hidden;
    overscroll-behavior: contain;
    background-color: var(--DM__theme-fill-color);
    z-index: 2;
    transition:
      opacity 0.3s,
      max-height 0.3s;
    opacity: 0;

    @include scrollBar();

    &--show {
      transition:
        opacity 0.5s,
        max-height 0.5s;
      max-height: 200px;
      overflow-y: auto;
      border-color: var(--DM__theme-hover-color);
      opacity: 1;
      @media (min-width: $screenSM) {
        max-height: 80vh;
      }
    }
  }

  .hide-input {
    opacity: 0;
    position: absolute;
    z-index: -1;
  }

  .item-square-selector {
    height: 2.5rem;
    width: 100%;
    border-color: var(--DM__theme-separator-color);
    text-decoration-color: var(--DM__theme-slider-color);
    @media (any-hover: hover) {
      &:hover {
        border-left: 4px solid var(--DM__theme-slider-color);
        background-color: #f9fafb;
        color: #1f2937;
      }
    }
  }

  .hide-input:checked + .item-square-selector {
    border-left: 4px solid var(--DM__theme-slider-color);
  }

  .hide-input:focus + .item-square-selector {
    text-decoration: underline;
    text-decoration-color: var(--DM__theme-slider-color);
    text-underline-offset: 3px;
    text-decoration-thickness: 2px;
  }
}
</style>
